import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Fade,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import apiService from "../../service/Api";
import { tokens } from "../../theme";
import TVChartCard from "./components/TVChartCard";

interface DashboardData {
  total_bookings: number;
  total_streams: number;
  total_contacts: number;
  total_venues: number;
  booking_trends?: {
    labels: string[];
    data: number[];
    stream_data: number[];
  };
}
interface PaginatedContacts {
  count: number;
  results: Array<{ contact_type: string | null }>;
}
interface PaginatedVenues {
  count: number;
  results: Array<{ venue_type: string | null; has_power: boolean | null }>;
}
interface PaginatedStreams {
  count: number;
  results: Array<{ booking?: { booking_type: string | null } }>;
}

const OVERVIEW_DURATION = 12000; 
const DETAIL_DURATION = 7000;

const slideInFromRight = keyframes`
   from { transform: scale(0.97); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
`;

const slideOutToLeft = keyframes`
  from { transform: scale(1);    opacity: 1; }
  to   { transform: scale(1.02); opacity: 0; }
`;


type Stage = "overview" | "detail-0" | "detail-1" | "detail-2";
const STAGES: Stage[] = [
  "overview",
  "detail-0",
  "detail-1",
  "detail-2",
];

const TVDashboard: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [data, setData] = useState<DashboardData | null>(null);
  const [contactsData, setContactsData] = useState<PaginatedContacts | null>(
    null,
  );
  const [venuesData, setVenuesData] = useState<PaginatedVenues | null>(null);
  const [streamsData, setStreamsData] = useState<PaginatedStreams | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stageIndex, setStageIndex] = useState(0);
  const [animState, setAnimState] = useState<"in" | "out">("in");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const advanceStage = useCallback(() => {
    setAnimState("out");
    timerRef.current = setTimeout(() => {
      setStageIndex((i) => (i + 1) % STAGES.length);
      setAnimState("in");
    }, 600);
  }, []);

  useEffect(() => {
    const currentStage = STAGES[stageIndex];
    const duration =
      currentStage === "overview" ? OVERVIEW_DURATION : DETAIL_DURATION;
    timerRef.current = setTimeout(advanceStage, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [stageIndex, advanceStage]);

  const fetchAllData = useCallback(async () => {
    try {
      const [statsResponse, contactsResponse, venuesResponse, streamsResponse] =
        await Promise.all([
          apiService.get<DashboardData>(
            "/api/management/dashboard/statistics/",
          ),
          apiService.get<PaginatedContacts>(
            "/api/contacts/contact/?page_size=100",
          ),
          apiService.get<PaginatedVenues>("/api/venues/venue/?page_size=100"),
          apiService.get<PaginatedStreams>(
            "/api/streams/stream/?page_size=100",
          ),
        ]);
      setData(statsResponse.data);
      setContactsData(contactsResponse.data);
      setVenuesData(venuesResponse.data);
      setStreamsData(streamsResponse.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch TV dashboard data:", err);
      setError("Connection lost. Retrying...");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    const dataInterval = setInterval(fetchAllData, 60000);
    return () => clearInterval(dataInterval);
  }, [fetchAllData]);

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.primary[100],
        }}
      >
        <CircularProgress size={50} thickness={2} sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }
  if (error && !data) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.primary[900],
          p: 3,
        }}
      >
        <Alert
          severity="error"
          sx={{ fontSize: "2rem", "& .MuiAlert-icon": { fontSize: "3rem" } }}
        >
          {error}
        </Alert>
      </Box>
    );
  }
  if (!data) return null;

  const chartLabels = (data.booking_trends?.labels || []).map((label) => {
    if (label.includes("-")) {
      const parts = label.split("-");
      if (parts.length >= 3) return `${parts[1]}/${parts[2]}`;
    }
    return label;
  });

  const bookingData = data.booking_trends?.data || [];
  const streamData = data.booking_trends?.stream_data || [];
  const maxBooking = Math.max(...(bookingData.length ? bookingData : [10]));
  const maxStream = Math.max(...(streamData.length ? streamData : [10]));

  const dynamicContactPieData = Object.entries(
    contactsData?.results.reduce(
      (acc, contact) => {
        const type = contact.contact_type || "unknown";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    ) || {},
  ).map(([type, count], index) => ({
    id: index,
    value: count,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    color:
      type === "company"
        ? "#8b5cf6"
        : type === "individual"
          ? "#10b981"
          : "#64748b",
  }));

  const dynamicVenueTypeData = Object.entries(
    venuesData?.results.reduce(
      (acc, venue) => {
        const type = venue.venue_type || "unknown";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    ) || {},
  )
    .map(([type, count], index) => ({
      id: index,
      type: type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const dynamicStreamTypeData = Object.entries(
    streamsData?.results.reduce(
      (acc, stream) => {
        const rawType = stream.booking?.booking_type || "Unknown";
        const type =
          rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    ) || {},
  ).map(([type, count], index) => {
    const chartColors = ["#3b82f6", "#8b5cf6", "#ec4899", "#10b981", "#f59e0b"];
    return {
      id: index,
      value: count,
      label: type,
      color: chartColors[index % chartColors.length],
    };
  });

  const tickStyle = { fontSize: 14, fill: colors.grey[400] };

  const currentStage = STAGES[stageIndex];

  const animSx = {
    animation:
      animState === "in"
        ? `${slideInFromRight} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both`
        : `${slideOutToLeft} 0.5s ease-in both`,
  };


  const bookingsContent = (
    <TVChartCard
      title="Total Bookings"
      total={data.total_bookings}
      color="#8b5cf6"
    >
      <Box sx={{ flexGrow: 1, minHeight: 0, width: "100%", height: "100%" }}>
        <LineChart
          xAxis={[
            {
              scaleType: "point",
              data: chartLabels,
              height: 60,
              tickLabelStyle: { ...tickStyle, angle: -45, textAnchor: "end" },
            },
          ]}
          yAxis={[
            {
              max: maxBooking + Math.ceil(maxBooking * 0.2),
              tickLabelStyle: { ...tickStyle, fontSize: 16 },
            },
          ]}
          series={[
            { data: bookingData, color: "#8b5cf6", area: true, showMark: true },
          ]}
          margin={{ top: 20, bottom: 60, left: 50, right: 30 }}
          sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.2 } }}
        />
      </Box>
    </TVChartCard>
  );

  const streamsContent = (
    <TVChartCard
      title="Total Streams"
      total={data.total_streams}
      color="#3b82f6"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexGrow: 1,
          width: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1.5,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.grey[400],
              textAlign: "center",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            Volume Trend
          </Typography>
          <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: chartLabels,
                  height: 60,
                  tickLabelStyle: {
                    ...tickStyle,
                    angle: -45,
                    textAnchor: "end",
                  },
                },
              ]}
              yAxis={[
                {
                  max: maxStream + Math.ceil(maxStream * 0.2),
                  tickLabelStyle: tickStyle,
                },
              ]}
              series={[
                {
                  data: streamData,
                  color: "#3b82f6",
                  area: true,
                  showMark: true,
                },
              ]}
              margin={{ top: 10, bottom: 60, left: 40, right: 20 }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: "1px",
            height: "80%",
            bgcolor: colors.primary[700],
            mx: 1,
            alignSelf: "center",
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.grey[400],
              textAlign: "center",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            By Type
          </Typography>
          <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
            <PieChart
              series={[
                {
                  data: dynamicStreamTypeData,
                  innerRadius: 60,
                  outerRadius: 110,
                  paddingAngle: 3,
                  cornerRadius: 4,
                },
              ]}
              margin={{ top: 10, bottom: 40, left: 10, right: 10 }}
            />
          </Box>
        </Box>
      </Box>
    </TVChartCard>
  );

  const contactsContent = (
    <TVChartCard
      title="Total Contacts"
      total={contactsData?.count || data.total_contacts}
      color="#10b981"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
          minHeight: 0,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: colors.grey[400],
            textAlign: "center",
            fontWeight: 700,
            lineHeight: 1,
            mb: 1,
          }}
        >
          By Type
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
          <PieChart
            series={[
              {
                data: dynamicContactPieData,
                innerRadius: 80,
                outerRadius: 140,
                paddingAngle: 5,
                cornerRadius: 4,
              },
            ]}
            margin={{ top: 10, bottom: 40, left: 10, right: 10 }}
          />
        </Box>
      </Box>
    </TVChartCard>
  );

  const venuesContent = (
    <TVChartCard
      title="Total Venues"
      total={venuesData?.count || data.total_venues}
      color="#f59e0b"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
          minHeight: 0,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: colors.grey[400],
            textAlign: "center",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          By Type
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
          {dynamicVenueTypeData.length > 0 ? (
            <BarChart
              layout="horizontal"
              yAxis={[
                {
                  scaleType: "band",
                  data: dynamicVenueTypeData.map((d) => d.type),
                  width: 150,
                  tickLabelStyle: { ...tickStyle, fontWeight: 600 },
                },
              ]}
              xAxis={[{ tickLabelStyle: tickStyle, tickMinStep: 1 }]}
              series={[
                {
                  data: dynamicVenueTypeData.map((d) => d.count),
                  color: "#f59e0b",
                },
              ]}
              margin={{ top: 10, bottom: 30, left: 80, right: 40 }}
              borderRadius={4}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ color: colors.grey[400] }}
              align="center"
              mt={4}
            >
              No data
            </Typography>
          )}
        </Box>
      </Box>
    </TVChartCard>
  );

  const detailCards = [
    bookingsContent,
    streamsContent,
    venuesContent,
  ];

  return (
    <Fade in={true} timeout={1000}>
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
          backgroundColor: colors.primary[100],
          p: 3,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            px: 2,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: colors.primary[900],
              letterSpacing: "-0.02em",
            }}
          >
            MUZUKURU FUNERAL STREAMING
          </Typography>
        </Box>

        {/* Overview — all 4 cards */}
        {currentStage === "overview" && (
          <Box
            key="overview"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: 3,
              flexGrow: 1,
              minHeight: 0,
              ...animSx,
            }}
          >
            <Box sx={{ minHeight: 0, height: "100%" }}>{bookingsContent}</Box>
            <Box sx={{ minHeight: 0, height: "100%" }}>{streamsContent}</Box>
            <Box sx={{ minHeight: 0, height: "100%" }}>{contactsContent}</Box>
            <Box sx={{ minHeight: 0, height: "100%" }}>{venuesContent}</Box>
          </Box>
        )}

        {currentStage !== "overview" && (
          <Box
            key={currentStage}
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              ...animSx,
            }}
          >
            {detailCards[parseInt(currentStage.split("-")[1])]}
          </Box>
        )}
      </Box>
    </Fade>
  );
};

export default TVDashboard;
